/**
 * MobileOnboarding
 *
 * Full-screen onboarding modal shown once on first native iOS launch after
 * sign-in.  Guides users through:
 *
 *   Step 1 (all roles):  Enable push notifications
 *   Step 2 (clients):    Connect Apple Health
 *
 * Each step has an "Allow" button that triggers the OS permission prompt and
 * a "Skip" button to proceed without granting permission.  After all steps a
 * "Get started" screen is shown for one second before the modal self-closes.
 *
 * On web or after onboarding is complete the component renders nothing.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell, HeartPulse, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/shared/ui/button";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useHealthKit } from "@/hooks/useHealthKit";
import { useAuth } from "@/hooks/useAuth";

type OnboardingStepId = "push" | "health" | "done";

interface MobileOnboardingProps {
  /** Injected in tests; defaults to the Capacitor native-detection logic */
  isNativeOverride?: boolean;
}

export const MobileOnboarding: React.FC<MobileOnboardingProps> = ({
  isNativeOverride,
}) => {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { hasCompletedOnboarding, markOnboardingComplete } =
    useOnboardingStatus();
  const { register, isNative } = usePushNotifications();
  const { requestPermissionsAndSync, isSupported: healthSupported } =
    useHealthKit();

  const isNativePlatform =
    isNativeOverride !== undefined ? isNativeOverride : isNative;
  const isClient = profile?.role === "client";

  // Determine first step; only skip to health if also a client
  const firstStep: OnboardingStepId = "push";

  const [step, setStep] = useState<OnboardingStepId>(firstStep);
  const [pushLoading, setPushLoading] = useState(false);
  const [healthLoading, setHealthLoading] = useState(false);

  // Auto-close after "done" screen
  useEffect(() => {
    if (step === "done") {
      const timer = setTimeout(() => {
        markOnboardingComplete();
      }, 1400);
      return () => clearTimeout(timer);
    }
  }, [step, markOnboardingComplete]);

  const handleAllowPush = useCallback(async () => {
    setPushLoading(true);
    try {
      await register();
    } finally {
      setPushLoading(false);
      setStep(isClient && healthSupported ? "health" : "done");
    }
  }, [register, isClient, healthSupported]);

  const handleSkipPush = useCallback(() => {
    setStep(isClient && healthSupported ? "health" : "done");
  }, [isClient, healthSupported]);

  const handleAllowHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      await requestPermissionsAndSync(30);
    } finally {
      setHealthLoading(false);
      setStep("done");
    }
  }, [requestPermissionsAndSync]);

  const handleSkipHealth = useCallback(() => {
    setStep("done");
  }, []);

  // Don't render on web or when onboarding is already complete
  if (!isNativePlatform || hasCompletedOnboarding || !profile) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background px-8"
      role="dialog"
      aria-modal="true"
      aria-label={t("onboarding.title")}
    >
      {step === "push" && (
        <OnboardingStep
          icon={<Bell className="h-16 w-16 text-primary" />}
          title={t("onboarding.pushTitle")}
          description={t("onboarding.pushDesc")}
          stepLabel={
            isClient && healthSupported
              ? t("onboarding.stepOf", { current: 1, total: 2 })
              : undefined
          }
          primaryLabel={
            pushLoading ? t("common.loading") : t("onboarding.pushAllow")
          }
          primaryDisabled={pushLoading}
          onPrimary={handleAllowPush}
          skipLabel={t("onboarding.pushSkip")}
          onSkip={handleSkipPush}
        />
      )}

      {step === "health" && (
        <OnboardingStep
          icon={<HeartPulse className="h-16 w-16 text-primary" />}
          title={t("onboarding.healthTitle")}
          description={t("onboarding.healthDesc")}
          stepLabel={t("onboarding.stepOf", { current: 2, total: 2 })}
          primaryLabel={
            healthLoading ? t("common.loading") : t("onboarding.healthAllow")
          }
          primaryDisabled={healthLoading}
          onPrimary={handleAllowHealth}
          skipLabel={t("onboarding.healthSkip")}
          onSkip={handleSkipHealth}
        />
      )}

      {step === "done" && (
        <div className="flex flex-col items-center gap-6 text-center">
          <CheckCircle2 className="h-16 w-16 text-success" />
          <div className="space-y-2">
            <h2 className="text-2xl font-bold">{t("onboarding.doneTitle")}</h2>
            <p className="text-muted-foreground">{t("onboarding.doneDesc")}</p>
          </div>
          <Button onClick={markOnboardingComplete} className="w-full max-w-xs">
            {t("onboarding.getStarted")}
          </Button>
        </div>
      )}
    </div>
  );
};

/* ── Internal step layout ─────────────────────────────────────────────────── */

interface OnboardingStepProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  stepLabel?: string;
  primaryLabel: string;
  primaryDisabled?: boolean;
  onPrimary: () => void;
  skipLabel: string;
  onSkip: () => void;
}

const OnboardingStep: React.FC<OnboardingStepProps> = ({
  icon,
  title,
  description,
  stepLabel,
  primaryLabel,
  primaryDisabled,
  onPrimary,
  skipLabel,
  onSkip,
}) => (
  <div className="flex w-full max-w-sm flex-col items-center gap-6 text-center">
    {stepLabel && <p className="text-xs text-muted-foreground">{stepLabel}</p>}
    <div className="rounded-full bg-primary/10 p-6">{icon}</div>
    <div className="space-y-2">
      <h2 className="text-2xl font-bold">{title}</h2>
      <p className="text-muted-foreground">{description}</p>
    </div>
    <div className="flex w-full flex-col gap-3">
      <Button className="w-full" onClick={onPrimary} disabled={primaryDisabled}>
        {primaryLabel}
      </Button>
      <Button variant="ghost" className="w-full" onClick={onSkip}>
        {skipLabel}
      </Button>
    </div>
  </div>
);
